import { DependenciesVersionsTable } from '@/ui/components/DependenciesVersionsTable';
import { classifiersListForConfigurationTransfer } from '@/ui/utils/classifiersListForConfigurationTransfer';
import { showAlertDialog } from '@juvare/common-web-ui/DialogHelper';
import { HeaderItem } from '@juvare/common-web-ui/GlobalApp';
import { requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { requireSystemAdmin } from '@juvare/contact-management/backend/features/permissions/shared/permissions';
import { ConfigurationTransferPage } from '@juvare/platform-sdk-react/ui/features/configuration-transfer';
import { EditablePagesPage } from '@juvare/platform-sdk-react/ui/features/editable-pages';
import { EditableSettingsAdminPage } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { getUserState } from '@juvare/platform-sdk-react/ui/stores';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SmsFailedOutlinedIcon from '@mui/icons-material/SmsFailedOutlined';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavigateFunction, RouteObject } from 'react-router';
import { ConfigurationsPage, useConfigurationPages } from '../features/classifiers-admin-pages';

interface Result {
    settingsRoutes: RouteObject[];
    settingsHeaderItems: HeaderItem[];
}

interface MenuItems {
    label: string;
    navigateTo?: string;
    onClick?: (navigate: NavigateFunction) => void;
    'data-test'?: string;
}

export function useSettingsMenu(): Result {
    const { t } = useTranslation();
    const userState = useSelector(getUserState);
    const settingsRoutes: RouteObject[] = [];
    const settingsHeaderItems: HeaderItem[] = [];
    const menuItems: MenuItems[] = [];
    const configurationPages = useConfigurationPages();
    const canManageEditablePages = requireAnySystemPermission(userState, 'manage-editable-pages');

    if (canManageEditablePages) {
        settingsRoutes.push({
            path: 'editable-pages/*',
            element: (
                <EditablePagesPage
                    title={t('appHeader.settings.supportPages')}
                    // TODO: use new permission framework with CNEO-3859
                    canCreate={requireSystemAdmin(userState)}
                    canDelete={requireSystemAdmin(userState)}
                    tags={[
                        {
                            value: 'landingPage',
                            label: t('editablePagesTags.landingPage'),
                        },
                        {
                            value: 'supportCenter',
                            label: t('editablePagesTags.supportCenter'),
                        },
                        {
                            value: 'login',
                            label: t('editablePagesTags.login'),
                        },
                    ]}
                />
            ),
        });
        settingsHeaderItems.push({
            icon: <SmsFailedOutlinedIcon />,
            label: t('appHeader.settings.supportPages'),
            navigateTo: '/editable-pages',
        });
    }

    if (
        userState &&
        requireAnySystemPermission(
            userState,
            'manage-all-classifiers',
            'manage-credentialing-classifiers',
            'manage-mission-classifiers',
            'manage-mission-position-classifier',
            'manage-mission-location-classifier',
            'manage-mission-location-type-classifier',
            'manage-mission-type-classifier',
        ) &&
        configurationPages.length > 0
    ) {
        settingsRoutes.push({
            path: 'configurations/*',
            element: <ConfigurationsPage />,
        });
        configurationPages.forEach((page) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const Component = page.component as React.FC<any>;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const props: Record<string, any> = {};
            if (page.name === 'state') props.enableRegisterState = true;
            if (page.name === 'region') props.includeTerritories = true;
            settingsRoutes.push({
                path: 'configurations/' + page.path + '/*',
                element: <Component {...props} />,
            });
        });
        menuItems.push({
            label: t('appHeader.settings.configurations'),
            navigateTo: '/configurations',
        });
    }

    if (userState && requireAnySystemPermission(userState, 'manage-settings', 'manage-system-settings')) {
        settingsRoutes.push({
            path: 'settings',
            element: <EditableSettingsAdminPage />,
        });
        menuItems.push({
            label: t('appHeader.settings.settings'),
            navigateTo: '/settings',
        });
    }

    if (requireAnySystemPermission(userState, 'view-versions')) {
        menuItems.push({
            label: t('appHeader.settings.versionInformation'),
            onClick: () => {
                showAlertDialog({
                    title: t('appHeader.settings.versionInformation'),
                    content: <DependenciesVersionsTable />,
                });
            },
            'data-test': 'toolbar-versions-item',
        });
    }

    if (requireAnySystemPermission(userState, 'configuration-transfer')) {
        settingsRoutes.push({
            path: 'configuration-transfer',
            element: (
                <ConfigurationTransferPage
                    features={['editable-settings', 'editable-pages', 'classifiers']}
                    classifiers={classifiersListForConfigurationTransfer}
                />
            ),
        });
        menuItems.push({
            label: t('appHeader.settings.configurationTransfer'),
            navigateTo: '/configuration-transfer',
            'data-test': 'toolbar-configuration-transfer-item',
        });
    }

    if (menuItems.length) {
        settingsHeaderItems.push({
            icon: <MoreHorizIcon />,
            label: 'More',
            menuItems,
            'data-test': 'settings-button',
        });
    }

    return { settingsRoutes, settingsHeaderItems };
}
